import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/PasswordInput';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const rules = [
  { test: (p) => p.length >= 8, label: 'Mínimo 8 caracteres' },
  { test: (p) => /\d/.test(p), label: 'Pelo menos 1 número' },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: 'Pelo menos 1 caractere especial' }
];

export default function AdminInviteAcceptPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { setAuthFromTokenResponse } = useAuth();

  const [form, setForm] = useState({ full_name: '', password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);

  const allPassed =
    rules.every((r) => r.test(form.password)) &&
    form.password === form.confirm_password &&
    form.full_name.trim().length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Convite inválido.');
      return;
    }
    if (!allPassed) return;
    setSubmitting(true);
    try {
      const res = await api.acceptAdminInvite(token, form.password, form.full_name.trim());
      if (res?.access_token) {
        // Persist session locally
        localStorage.setItem('token', res.access_token);
      }
      if (setAuthFromTokenResponse) setAuthFromTokenResponse(res);
      toast.success('Acesso administrativo ativado!');
      setTimeout(() => { window.location.href = '/admin'; }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Convite inválido ou expirado.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Convite inválido</CardTitle>
            <CardDescription>O link não contém um token válido.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">Voltar ao início</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-950 py-12 px-4" data-testid="admin-invite-accept-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-red-500 flex items-center justify-center mb-3">
            <ShieldCheck className="w-7 h-7 text-white" />
          </div>
          <CardTitle>Ativar acesso administrativo</CardTitle>
          <CardDescription>Defina sua senha para acessar o painel do Akali AI.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-accept-form">
            <div>
              <Label htmlFor="full_name">Nome completo</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
                data-testid="accept-fullname-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="accept-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmar senha</Label>
              <PasswordInput
                id="confirm_password"
                autoComplete="new-password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                data-testid="accept-confirm-input"
              />
            </div>

            <ul className="text-sm space-y-1">
              {rules.map((r, i) => {
                const ok = r.test(form.password);
                return (
                  <li key={i} className={ok ? 'text-emerald-600' : 'text-slate-400'}>
                    {ok ? '✓' : '○'} {r.label}
                  </li>
                );
              })}
              {form.password && form.confirm_password && (
                <li className={form.password === form.confirm_password ? 'text-emerald-600' : 'text-red-500'}>
                  {form.password === form.confirm_password ? '✓' : '✗'} Senhas coincidem
                </li>
              )}
            </ul>

            <Button
              type="submit"
              disabled={!allPassed || submitting}
              className="w-full bg-gradient-to-r from-amber-500 to-red-500 text-white"
              data-testid="accept-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ativando…</> : <>Ativar acesso</>}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
